import React, { useRef } from "react";
import {
  useEditor,
  EditorContent,
  useEditorState,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import { Color } from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";

import {
  Box,
  IconButton,
  Tooltip,
  Divider,
  MenuItem,
  Select,
  FormControl,
} from "@mui/material";

// Icons
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import ImageIcon from "@mui/icons-material/Image";
import FormatClearIcon from "@mui/icons-material/FormatClear";

// Resizable Image Component
const ResizableImageComponent = ({ node, updateAttributes }) => {
  const imgRef = useRef(null);

  const handleResize = (e) => {
    const newWidth = e.target.value;
    updateAttributes({ width: `${newWidth}px` });
  };

  return (
    <NodeViewWrapper
      className="resizable-image"
      style={{ position: "relative" }}
    >
      <img
        ref={imgRef}
        src={node.attrs.src}
        alt={node.attrs.alt}
        style={{ width: node.attrs.width || "300px", maxWidth: "100%" }}
      />
      <input
        type="range"
        min="100"
        max="800"
        value={parseInt(node.attrs.width || "300")}
        onChange={handleResize}
        style={{ position: "absolute", bottom: -20, left: 0, width: "100%" }}
      />
    </NodeViewWrapper>
  );
};

const ResizableImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: "300px",
        parseHTML: (el) => el.getAttribute("width"),
        renderHTML: (attrs) => ({ width: attrs.width }),
      },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageComponent);
  },
});

// Toolbar Component
const Toolbar = ({ editor }) => {
  const fileInputRef = useRef(null);

  const editorState = useEditorState({
    editor,
    selector: (ctx) => ({
      isBold: ctx.editor.isActive("bold"),
      isItalic: ctx.editor.isActive("italic"),
      isUnderline: ctx.editor.isActive("underline"),
      isStrike: ctx.editor.isActive("strike"),
      isBulletList: ctx.editor.isActive("bulletList"),
      isOrderedList: ctx.editor.isActive("orderedList"),
      isBlockquote: ctx.editor.isActive("blockquote"),
      headingLevel: ctx.editor.getAttributes("heading").level || 0,
      color: ctx.editor.getAttributes("textStyle").color || "#000000",
    }),
  });

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file || !editor) return;

    const reader = new FileReader();
    reader.onload = () => {
      editor
        .chain()
        .focus()
        .setImage({ src: reader.result, width: "300px" })
        .run();
    };
    reader.readAsDataURL(file);
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  if (!editor) return null;

  return (
    <Box
      sx={{
        display: "flex",
        gap: 0.5,
        padding: "8px",
        borderBottom: "1px solid #ddd",
        flexWrap: "wrap",
        alignItems: "center",
        position: "sticky",
        top: 0,
        zIndex: 2,
        backgroundColor: "#fff",
      }}
    >
      {/* Text Formatting */}
      <Tooltip title="Bold (Ctrl+B)">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleBold().run()}
          sx={{
            backgroundColor: editorState.isBold ? "#e0e0e0" : "transparent",
          }}
        >
          <FormatBoldIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Italic (Ctrl+I)">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          sx={{
            backgroundColor: editorState.isItalic ? "#e0e0e0" : "transparent",
          }}
        >
          <FormatItalicIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Underline (Ctrl+U)">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          sx={{
            backgroundColor: editorState.isUnderline
              ? "#e0e0e0"
              : "transparent",
          }}
        >
          <FormatUnderlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Strikethrough">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          sx={{
            backgroundColor: editorState.isStrike ? "#e0e0e0" : "transparent",
          }}
        >
          <StrikethroughSIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Divider
        orientation="vertical"
        flexItem
        sx={{ mx: 0.5 }}
      />

      {/* Heading Dropdown */}
      <FormControl
        size="small"
        sx={{ minWidth: 100 }}
      >
        <Select
          value={editorState.headingLevel}
          onChange={(e) => {
            const level = e.target.value;
            if (level === 0) {
              editor.chain().focus().setParagraph().run();
            } else {
              editor.chain().focus().setHeading({ level }).run();
            }
          }}
          sx={{ height: 32, fontSize: "12px" }}
        >
          <MenuItem value={0}>Paragraph</MenuItem>
          <MenuItem value={1}>Heading 1</MenuItem>
          <MenuItem value={2}>Heading 2</MenuItem>
          <MenuItem value={3}>Heading 3</MenuItem>
          <MenuItem value={4}>Heading 4</MenuItem>
        </Select>
      </FormControl>

      <Divider
        orientation="vertical"
        flexItem
        sx={{ mx: 0.5 }}
      />

      {/* Lists */}
      <Tooltip title="Bullet List">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          sx={{
            backgroundColor: editorState.isBulletList
              ? "#e0e0e0"
              : "transparent",
          }}
        >
          <FormatListBulletedIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Numbered List">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          sx={{
            backgroundColor: editorState.isOrderedList
              ? "#e0e0e0"
              : "transparent",
          }}
        >
          <FormatListNumberedIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Blockquote">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          sx={{
            backgroundColor: editorState.isBlockquote
              ? "#e0e0e0"
              : "transparent",
          }}
        >
          <FormatQuoteIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Divider
        orientation="vertical"
        flexItem
        sx={{ mx: 0.5 }}
      />

      {/* Color Picker */}
      <Tooltip title="Text Color">
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <input
            type="color"
            value={editorState.color}
            onChange={(e) =>
              editor.chain().focus().setColor(e.target.value).run()
            }
            style={{
              width: 32,
              height: 32,
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          />
        </Box>
      </Tooltip>

      <Divider
        orientation="vertical"
        flexItem
        sx={{ mx: 0.5 }}
      />

      {/* Other Elements */}
      <Tooltip title="Insert Image">
        <IconButton
          size="small"
          onClick={triggerFileInput}
        >
          <ImageIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Horizontal Rule">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
        >
          <HorizontalRuleIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Clear Formatting">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().clearNodes().run()}
        >
          <FormatClearIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleImageUpload}
      />
    </Box>
  );
};

// Main RichTextEditor Component
const RichTextEditor = ({ html = "", onChange, height = "100%" }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      ResizableImage.configure({
        inline: true,
        allowBase64: true,
      }),
    ],
    content: html,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      if (onChange) onChange(html);
    },
  });

  if (!editor) return null;

  return (
    <Box
      sx={{
        width: "100%",
        border: "1px solid #ddd",
        borderRadius: "4px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height,
      }}
    >
      <Toolbar editor={editor} />
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          padding: "12px",
          "& .ProseMirror": {
            minHeight: "100%",
            outline: "none",
            "& p": {
              margin: "0.5em 0",
            },
            "& h1": {
              fontSize: "2em",
              fontWeight: "bold",
              margin: "0.67em 0",
            },
            "& h2": {
              fontSize: "1.5em",
              fontWeight: "bold",
              margin: "0.75em 0",
            },
            "& h3": {
              fontSize: "1.17em",
              fontWeight: "bold",
              margin: "0.83em 0",
            },
            "& h4": {
              fontSize: "1em",
              fontWeight: "bold",
              margin: "1em 0",
            },
            "& ul, & ol": {
              paddingLeft: "2em",
              margin: "0.5em 0",
            },
            "& blockquote": {
              borderLeft: "4px solid #ddd",
              paddingLeft: "1em",
              marginLeft: 0,
              color: "#666",
              fontStyle: "italic",
            },
            "& hr": {
              border: "none",
              borderTop: "1px solid #ddd",
              margin: "1em 0",
            },
            "& img": {
              maxWidth: "100%",
              marginTop: "0.5em",
              marginBottom: "0.5em",
            },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};

export default RichTextEditor;
